/**
 * TranscriptProvider: o estado da transcrição da chamada (falas por pessoa, momentos, quem está falando, painel
 * e legenda), dentro da sala LiveKit.
 *
 * Fica ACIMA de onde a sala é recriada (ao ir para a janela flutuante e voltar a árvore desmonta e monta de novo,
 * como já acontece com o `PeerLeftTracker`): o texto, o painel aberto e a legenda não podem se perder nessa troca.
 *
 * De onde vem o texto: a lista inicial por REST (o que veio antes de o atendente entrar, ou enquanto esteve
 * fora) e as falas novas pelo canal de dados da sala. As duas se juntam pelo `id` da fala, sem duplicar, e a lista
 * é recarregada quando a sala reconecta (o canal pode ter perdido falas). Só vale o texto de quem tem identidade
 * `tx:` (ver `transcriptProtocol`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { TranscriptContext } from './transcriptContext';
import { isTranscribed, mergeSegments, normalizeSegment, parseTranscriptMessage } from './transcriptProtocol';
import { useTranscriberPresent } from './useTranscriberPresence';

/** Depois que a pessoa para de falar, o texto ainda demora 1 a 3 s: os pontinhos seguem até ele chegar (ou 6 s). */
export const PENDING_MS = 6000;

export const MOMENT_ERROR = 'Não foi possível marcar o momento.';

/**
 * Com a chamada em andamento, a Jev entra na sala em até ~5 s (o transcritor olha o banco a cada 5 s) mais o tempo de
 * conectar. Passado isto sem ela, a transcrição está indisponível e o painel avisa em vez de ficar vazio em silêncio.
 */
export const TRANSCRIBER_GRACE_MS = 20_000;

/** Enquanto a transcrição está indisponível, de quanto em quanto tempo o painel pergunta de novo o motivo. */
export const DIAGNOSIS_REFRESH_MS = 20_000;

const CAPTIONS_KEY = 'touchworks.videoCall.captions';

/** A escolha da legenda neste navegador: `true`, `false`, ou `null` (nunca escolheu). Tolera storage bloqueado. */
export function readCaptionsPref() {
    try {
        const value = window.localStorage.getItem(CAPTIONS_KEY);
        return value === '1' ? true : value === '0' ? false : null;
    } catch {
        return null;
    }
}

function writeCaptionsPref(value) {
    try {
        window.localStorage.setItem(CAPTIONS_KEY, value ? '1' : '0');
    } catch { /* storage bloqueado: a escolha vale só nesta sessão */ }
}

/** Une momentos por id, em ordem de hora, sem trazer de volta os que o atendente acabou de desfazer. */
const mergeMoments = (existing, incoming, removed) => {
    const byId = new Map(existing.map((m) => [m.id, m]));
    for (const moment of incoming) {
        if (moment && typeof moment.id === 'string' && Number.isFinite(moment.at) && !removed.has(moment.id)) byId.set(moment.id, moment);
    }
    return [...byId.values()].sort((a, b) => a.at - b.at);
};

/**
 * @param {{
 *   videoCallId: string, originAt?: string|null, active?: boolean, panelPref?: boolean|null,
 *   onPanelPrefChange?: (pref: boolean) => void, children: React.ReactNode,
 *   api: {
 *     getTranscript: (videoCallId: string) => Promise<{ segments?: object[], moments?: object[], startedAt?: string|null }>,
 *     addMoment: (videoCallId: string) => Promise<{ id: string, at: number }>,
 *     removeMoment: (input: { videoCallId: string, momentId: string }) => Promise<unknown>,
 *     getTranscriberStatus?: (videoCallId: string) => Promise<{ state: string|null, code?: string }>,
 *   },
 * }} props
 *   `originAt`: `firstJoinedAt` da chamada, a origem do "12:34" (o mesmo relógio do cronômetro). Sem ele vale o
 *   que o servidor devolve na lista inicial. `active`: a chamada está em andamento (a Jev deve estar na sala);
 *   com ela ausente depois de `TRANSCRIBER_GRACE_MS`, `transcriberState` vira `unavailable`. `panelPref` e
 *   `onPanelPrefChange`: se a janela (que precisa saber, para crescer) guarda a escolha de abrir o painel, vêm de lá;
 *   sem eles o provedor guarda sozinho (ver `panelState`). `api` vem de quem
 *   monta a sala (a janela do atendente): o provedor não conhece a camada de rede, e a página pública do convidado
 *   nunca a carrega.
 */
export function TranscriptProvider({
    videoCallId, originAt = null, active = true, panelPref: panelPrefProp, onPanelPrefChange, api, children,
}) {
    const room = useRoomContext();
    const [segments, setSegments] = useState([]);
    const [moments, setMoments] = useState([]);
    const [serverOrigin, setServerOrigin] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [speaking, setSpeaking] = useState([]); // [{ identity, name }] falando agora, só quem é transcrito
    const [pending, setPending] = useState([]); // [{ identity, name }] pararam de falar e o texto ainda não chegou
    // A última fala que chegou AO VIVO e quando chegou aqui: a legenda se guia pelo relógio deste navegador (o do
    // servidor pode divergir por segundos). O que veio da lista inicial é histórico e nunca vira legenda.
    const [liveSegment, setLiveSegment] = useState(null);
    const [lastMoment, setLastMoment] = useState(null);
    const [momentBusy, setMomentBusy] = useState(false);
    const [momentError, setMomentError] = useState(null);
    const [captionsPref, setCaptionsPrefState] = useState(readCaptionsPref);
    const [ownPanelPref, setOwnPanelPref] = useState(null);
    const controlled = panelPrefProp !== undefined;
    const panelPref = controlled ? panelPrefProp : ownPanelPref;

    const transcriberPresent = useTranscriberPresent();
    const [graceOver, setGraceOver] = useState(false);
    const [diagnosis, setDiagnosis] = useState(null); // { state, code } | null: por que a Jev não entrou

    const removedMoments = useRef(new Set());
    const pendingTimers = useRef(new Map());
    const speakingRef = useRef([]);
    const mounted = useRef(true);

    const load = useCallback(async () => {
        try {
            const data = await api.getTranscript(videoCallId);
            if (!mounted.current) return;
            const incoming = (data?.segments ?? []).map(normalizeSegment).filter(Boolean);
            setSegments((prev) => mergeSegments(prev, incoming));
            setMoments((prev) => mergeMoments(prev, data?.moments ?? [], removedMoments.current));
            const started = data?.startedAt ? Date.parse(data.startedAt) : NaN;
            setServerOrigin(Number.isFinite(started) ? started : null);
            setStatus('ready');
        } catch {
            // Já tem texto na tela: a falha de uma recarga não apaga nada. Sem texto, mostra o erro.
            if (mounted.current) setStatus((current) => (current === 'ready' ? current : 'error'));
        }
    }, [api, videoCallId]);

    useEffect(() => {
        mounted.current = true;
        const timers = pendingTimers.current;
        load();
        return () => {
            mounted.current = false;
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
        };
    }, [load]);

    // present: a Jev está na sala | waiting: a chamada ainda não começou | starting: entrando | unavailable: não entrou
    const transcriberState = transcriberPresent ? 'present' : !active ? 'waiting' : graceOver ? 'unavailable' : 'starting';

    // Chamada em andamento e a Jev ausente: dá o tempo de ela entrar; depois disso é falha, e o painel diz.
    useEffect(() => {
        setGraceOver(false);
        if (transcriberPresent || !active) return undefined;
        const timer = setTimeout(() => setGraceOver(true), TRANSCRIBER_GRACE_MS);
        return () => clearTimeout(timer);
    }, [transcriberPresent, active]);

    // A Jev não entrou: pergunta ao servidor o motivo (processo fora do ar, desligado, falha ao entrar...), e de novo de vez
    // em quando. Sem resposta (a rota falhou), o aviso fica genérico.
    const unavailable = transcriberState === 'unavailable';
    useEffect(() => {
        setDiagnosis(null);
        if (!unavailable || typeof api.getTranscriberStatus !== 'function') return undefined;
        let cancelled = false;
        const ask = async () => {
            try {
                const data = await api.getTranscriberStatus(videoCallId);
                if (!cancelled) setDiagnosis(data?.state ? { state: data.state, code: data.code ?? null } : null);
            } catch {
                if (!cancelled) setDiagnosis(null);
            }
        };
        ask();
        const timer = setInterval(ask, DIAGNOSIS_REFRESH_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [unavailable, api, videoCallId]);

    const clearPending = useCallback((identity) => {
        clearTimeout(pendingTimers.current.get(identity));
        pendingTimers.current.delete(identity);
        setPending((prev) => (prev.some((p) => p.identity === identity) ? prev.filter((p) => p.identity !== identity) : prev));
    }, []);

    useEffect(() => {
        if (!room) return undefined;

        const onData = (payload, participant, _kind, topic) => {
            const segment = parseTranscriptMessage({ payload, participant, topic });
            if (!segment) return;
            setSegments((prev) => mergeSegments(prev, [segment]));
            setLiveSegment({ segment, receivedAt: Date.now() });
            clearPending(segment.identity);
        };

        const onSpeakers = (speakers = []) => {
            const now = speakers.filter(isTranscribed).map((p) => ({ identity: p.identity, name: p.name || '' }));
            const stopped = speakingRef.current.filter((before) => !now.some((n) => n.identity === before.identity));
            speakingRef.current = now;
            setSpeaking(now);
            for (const person of stopped) {
                setPending((prev) => (prev.some((p) => p.identity === person.identity) ? prev : [...prev, person]));
                clearTimeout(pendingTimers.current.get(person.identity));
                pendingTimers.current.set(person.identity, setTimeout(() => clearPending(person.identity), PENDING_MS));
            }
        };

        room.on(RoomEvent.DataReceived, onData);
        room.on(RoomEvent.ActiveSpeakersChanged, onSpeakers);
        room.on(RoomEvent.Reconnected, load);
        return () => {
            room.off(RoomEvent.DataReceived, onData);
            room.off(RoomEvent.ActiveSpeakersChanged, onSpeakers);
            room.off(RoomEvent.Reconnected, load);
        };
    }, [room, load, clearPending]);

    const addMoment = useCallback(async () => {
        setMomentBusy(true);
        setMomentError(null);
        try {
            const moment = await api.addMoment(videoCallId);
            if (!mounted.current) return null;
            setMoments((prev) => mergeMoments(prev, [moment], removedMoments.current));
            setLastMoment(moment);
            return moment;
        } catch {
            if (mounted.current) setMomentError(MOMENT_ERROR);
            return null;
        } finally {
            if (mounted.current) setMomentBusy(false);
        }
    }, [api, videoCallId]);

    const undoMoment = useCallback(async (momentId) => {
        removedMoments.current.add(momentId);
        setMoments((prev) => prev.filter((m) => m.id !== momentId));
        setLastMoment((prev) => (prev?.id === momentId ? null : prev));
        try {
            await api.removeMoment({ videoCallId, momentId });
        } catch {
            // O servidor não confirmou: volta o marcador para a lista (a próxima recarga acerta o resto).
            removedMoments.current.delete(momentId);
            load();
        }
    }, [api, videoCallId, load]);

    const dismissMomentNotice = useCallback(() => { setLastMoment(null); setMomentError(null); }, []);

    const setPanelPref = useCallback((value) => {
        if (!controlled) setOwnPanelPref(value);
        onPanelPrefChange?.(value);
    }, [controlled, onPanelPrefChange]);

    const setCaptionsPref = useCallback((value) => {
        writeCaptionsPref(value);
        setCaptionsPrefState(value);
    }, []);

    const origin = useMemo(() => {
        const fromCall = originAt ? Date.parse(originAt) : NaN;
        return Number.isFinite(fromCall) ? fromCall : serverOrigin;
    }, [originAt, serverOrigin]);

    // Quem está falando agora OU acabou de falar e ainda não teve o texto entregue (sem repetir a pessoa).
    const indicators = useMemo(() => {
        const seen = new Set();
        return [...speaking, ...pending].filter((p) => (seen.has(p.identity) ? false : (seen.add(p.identity), true)));
    }, [speaking, pending]);

    const value = useMemo(() => ({
        videoCallId,
        status,
        transcriberState,
        transcriberDiagnosis: diagnosis,
        segments,
        moments,
        origin,
        indicators,
        liveSegment,
        localIdentity: room?.localParticipant?.identity ?? null,
        lastMoment,
        momentBusy,
        momentError,
        addMoment,
        undoMoment,
        dismissMomentNotice,
        reload: load,
        captionsPref,
        setCaptionsPref,
        panelPref,
        setPanelPref,
    }), [videoCallId, status, transcriberState, diagnosis, segments, moments, origin, indicators, liveSegment, room, lastMoment, momentBusy, momentError,
        addMoment, undoMoment, dismissMomentNotice, load, captionsPref, setCaptionsPref, panelPref, setPanelPref]);

    return <TranscriptContext.Provider value={value}>{children}</TranscriptContext.Provider>;
}
